/**
 * Adaptador de solicitudes (viáticos / anticipos).
 *
 * El back entrega hoy dos formas distintas del mismo recurso:
 *  - la forma "completa" (relaciones anidadas: user, project, categories[].pivot, ...)
 *  - la forma "reporte" (columnas planas prefijadas: request_folio, project_name, ...)
 *
 * Este módulo aplana ambas hacia un único modelo de front en camelCase, tolerando
 * nulos/ausencias, para que la vista nunca dependa de la forma del payload.
 */

export const REQUEST_STATUS = {
  CREATED: 'CREATED',
  SENT: 'SENT',
  IN_REVIEW: 'IN_REVIEW',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED'
}

/** Tipos de archivo (files_type_id) con visualización distinta. */
export const FILE_TYPE = {
  PDF: 3,
  COMPRESSED: [9, 12]
}

export function isCompressedFile (fileTypeId) {
  return FILE_TYPE.COMPRESSED.indexOf(Number(fileTypeId)) !== -1
}

export const FUNDING_TYPE = {
  PETTY_CASH: 'caja_chica',
  ADVANCE: 'anticipo',
  REFUND: 'reembolso'
}

/** Primer valor "presente" (ni undefined, ni null, ni cadena vacía). */
function firstDefined (values, fallback = null) {
  for (let i = 0; i < values.length; i++) {
    const value = values[i]
    if (value !== undefined && value !== null && value !== '') return value
  }
  return fallback
}

/** Número seguro: cualquier basura cae al valor por defecto. */
export function toNumber (value, fallback = 0) {
  const parsed = parseFloat(value)
  return isNaN(parsed) ? fallback : parsed
}

/** Normaliza cualquier fecha a 'YYYY-MM-DD'; nunca lanza. */
export function toIsoDate (value) {
  if (value === undefined || value === null || value === '') return ''
  if (typeof value === 'string') {
    const matched = value.match(/^\d{4}-\d{2}-\d{2}/)
    if (matched) return matched[0]
  }
  const date = new Date(value)
  if (isNaN(date.getTime())) return ''
  return date.toISOString().substr(0, 10)
}

function asArray (value) {
  return Array.isArray(value) ? value : []
}

function asObject (value) {
  return value && typeof value === 'object' ? value : {}
}

/**
 * Una categoría puede venir como relación con pivot o ya aplanada.
 * En ambos casos regresamos la misma forma de front.
 */
function buildCategoryModel (raw) {
  const source = asObject(raw)
  const pivot = asObject(source.pivot)

  return {
    id: firstDefined([pivot.id, source.id]),
    categoryId: firstDefined([pivot.categories_id, source.categories_id, source.id]),
    // El catálogo trae nombres con espacios al final ("4456600-IVA DEDUCIBLE ").
    name: String(firstDefined([source.name, pivot.name], '')).trim(),
    price: toNumber(firstDefined([pivot.price, source.price], 0)),
    quantity: toNumber(firstDefined([pivot.quantity, source.quantity], 0)),
    peopleCount: toNumber(firstDefined([pivot.number_people, source.number_people], 0)),
    notes: firstDefined([pivot.comment, source.comment], ''),
    startDate: toIsoDate(firstDefined([pivot.start_date, source.start_date])),
    endDate: toIsoDate(firstDefined([pivot.end_date, source.end_date])),
    amount: toNumber(firstDefined([pivot.amount, source.amount], 0))
  }
}

/**
 * Las categorías llegan de tres formas distintas según el endpoint:
 *  - arreglo de relaciones con pivot        (detalle de la solicitud)
 *  - arreglo ya aplanado                    (payload de guardado)
 *  - mapa { "NOMBRE CATEGORÍA": monto }     (reporte requests-vs-expenses)
 */
function buildCategoryCollection (raw) {
  if (Array.isArray(raw)) return raw.map(buildCategoryModel)

  if (raw && typeof raw === 'object') {
    return Object.keys(raw).map(name => {
      const category = buildCategoryModel(null)
      category.name = String(name).trim()
      category.amount = toNumber(raw[name], 0)
      return category
    })
  }

  return []
}

function buildExtensionModel (raw) {
  const source = asObject(raw)

  return {
    id: firstDefined([source.id]),
    folio: firstDefined([source.folio], ''),
    status: firstDefined([source.status], ''),
    startDate: toIsoDate(source.current_date),
    endDate: toIsoDate(source.end_date),
    hasAdvance: toNumber(source.is_advance, 0) === 1,
    advanceAmount: toNumber(source.amount_advance, 0),
    authorizedAmount: toNumber(source.amount_authorized, 0),
    categories: buildCategoryCollection(source.categories)
  }
}

function buildVehicleModel (raw) {
  const source = asObject(raw)

  return {
    vehicleId: firstDefined([source.gst_vehicle_id, source.vehicle_id]),
    licensePlate: firstDefined([source.license_plate], ''),
    initialMileage: toNumber(source.initial_milleage, 0),
    finalMileage: toNumber(source.final_milleage, 0),
    initialLiters: toNumber(source.fuel_liters, 0),
    finalLiters: toNumber(source.final_liters, 0),
    notes: firstDefined([source.description], '')
  }
}

function buildAttachmentModel (raw) {
  const source = asObject(raw)
  const file = asObject(source.file)

  return {
    id: firstDefined([source.id, source.file_id, file.id]),
    fileId: firstDefined([source.file_id, file.id]),
    fileTypeId: toNumber(firstDefined([file.files_type_id, source.files_type_id], 0)),
    file: source.file || null
  }
}

/** Deriva el origen del recurso (caja chica / anticipo / reembolso). */
export function resolveFundingType (raw) {
  const source = asObject(raw)
  const pettyCash = firstDefined([source.is_petty_cash, source.petty_cash_type], 0)
  if (toNumber(pettyCash, 0) === 1) return FUNDING_TYPE.PETTY_CASH
  if (toNumber(firstDefined([source.advance, source.amount_advance], 0)) > 0) return FUNDING_TYPE.ADVANCE
  return FUNDING_TYPE.REFUND
}

/** Modelo vacío: sirve para crear y como estado inicial del diálogo. */
export function emptyRequestModel () {
  return {
    id: null,
    folio: '',
    status: null,
    requestedById: null,
    requestedByName: '',
    assignedUserId: null,
    assignedUserName: '',
    requestDate: toIsoDate(new Date()),
    startDate: '',
    endDate: '',
    projectId: null,
    projectName: '',
    orderNumber: '',
    description: '',
    isTravel: false,
    travelTypeId: null,
    travelTypeName: '',
    origin: '',
    destination: '',
    isVehicle: false,
    vehicle: buildVehicleModel(null),
    categories: [],
    extensions: [],
    fundingType: FUNDING_TYPE.REFUND,
    totalAmount: 0,
    advanceAmount: 0,
    authorizedAmount: 0,
    validatorId: null,
    validatorName: '',
    validationComment: '',
    attachments: []
  }
}

/**
 * Construye el modelo de front a partir de cualquiera de las formas del back.
 * `raw` puede ser null: en ese caso regresa el modelo vacío.
 */
export function buildRequestModel (raw) {
  const source = asObject(raw)
  const model = emptyRequestModel()

  const requester = asObject(source.user)
  const assigned = asObject(firstDefined([source.user_assigned, source.assigned_user], {}))
  const project = asObject(source.project)
  const travelType = asObject(source.travel_type)
  const fuelRequest = asArray(firstDefined([source.fuel_requests, source.fuelRequests], []))[0]
  const validators = asArray(source.validators)
  const firstValidator = asObject(validators[0])
  const validatorPivot = asObject(firstValidator.pivot)

  model.id = firstDefined([source.id, source.request_id])
  model.folio = firstDefined([source.folio, source.request_folio], '')
  model.status = firstDefined([source.status, source.request_status])

  model.requestedById = firstDefined([source.user_id, requester.id])
  model.requestedByName = firstDefined([requester.name, source.request_user_name], '')
  model.assignedUserId = firstDefined([source.assigned_user_id, assigned.id])
  // Sin asignado se queda vacío (como la vista original); el nombre sale del catálogo de usuarios.
  model.assignedUserName = firstDefined([assigned.name, source.assigned_user_name], '')

  model.requestDate = toIsoDate(firstDefined([source.creation_date, source.request_creation_date]))
  model.startDate = toIsoDate(firstDefined([source.start_date, source.request_start_date]))
  model.endDate = toIsoDate(firstDefined([source.end_date, source.request_end_date]))

  model.projectId = firstDefined([source.project_id, project.id])
  model.projectName = firstDefined([project.name, source.project_name], '')
  model.orderNumber = firstDefined([source.order_number, source.request_order_number], '')
  model.description = firstDefined([source.description, source.request_description], '')

  model.travelTypeId = firstDefined([travelType.id, source.gst_travel_type_id])
  model.travelTypeName = firstDefined([travelType.name], '')
  model.origin = firstDefined([source.origin, source.request_origin], '')
  model.destination = firstDefined([source.destination, source.request_destination], '')
  // El reporte no manda tipo de viaje; si trae origen/destino igual hay que mostrarlos.
  model.isTravel = model.travelTypeId !== null || !!model.origin || !!model.destination

  model.isVehicle = !!fuelRequest
  model.vehicle = buildVehicleModel(fuelRequest)

  model.categories = buildCategoryCollection(
    firstDefined([source.categories, source.request_categories], null)
  )
  model.extensions = asArray(source.extensions).map(buildExtensionModel)

  model.fundingType = resolveFundingType(source)
  // El detalle por folio no manda total: igual que la vista original, es la suma de categorías.
  model.totalAmount = toNumber(
    firstDefined([source.totalAmount, source.total_amount, source.request_total], null),
    sumCategories(model.categories)
  )
  model.advanceAmount = toNumber(firstDefined([source.amount_advance, source.advance], 0))
  model.authorizedAmount = toNumber(source.amount_authorized, 0)

  model.validatorId = firstDefined([source.validator_id, validatorPivot.validators_id])
  model.validatorName = firstDefined([asObject(firstValidator.user).name], '')
  model.validationComment = firstDefined([source.comment, validatorPivot.comment], '')

  model.attachments = asArray(source.attachments).map(buildAttachmentModel)

  return model
}

/** Suma de importes de una colección de categorías del modelo de front. */
export function sumCategories (categories) {
  return asArray(categories).reduce((total, category) => total + toNumber(category.amount, 0), 0)
}

/** Anticipo total = anticipo de la solicitud + anticipos de ampliaciones aprobadas. */
export function calculateTotalAdvance (model) {
  const source = asObject(model)
  const approved = asArray(source.extensions)
    .filter(extension => extension.status === REQUEST_STATUS.APPROVED)
    .reduce((total, extension) => total + toNumber(extension.advanceAmount, 0), 0)

  return toNumber(source.advanceAmount, 0) + approved
}



// WEBPACK FOOTER //
// ./src/components/requests/request.model.js